import { useEffect, useState } from "react";
import { Lock, RefreshCw } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Seo from "@/components/Seo";

const Admin = () => {
  const { t } = useLanguage();
  const a = t.admin;
  const [key, setKey] = useState(() => sessionStorage.getItem("mk-admin-key") || "");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        headers: { "X-Admin-Key": k },
      });
      if (res.status === 401) {
        setError(a.wrong);
        setLeads(null);
        sessionStorage.removeItem("mk-admin-key");
      } else if (res.ok) {
        setLeads(await res.json());
        sessionStorage.setItem("mk-admin-key", k);
      } else {
        setError("Error");
      }
    } catch {
      setError("Error");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unlock = () => load(key);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="admin-page">
      <Seo title="Leads — Marina Knows" description="Internal leads view." />
      <h1 className="font-serif text-4xl md:text-5xl text-ink mb-10" data-testid="admin-title">{a.title}</h1>

      {!leads && (
        <div className="bg-white border border-stone rounded-2xl p-8 max-w-md">
          <Lock size={22} className="text-bronze mb-4" aria-hidden />
          <label htmlFor="admin-key" className="block font-mono text-xs uppercase tracking-[0.2em] text-stone-fg mb-1">
            {a.passcode}
          </label>
          <input
            id="admin-key"
            data-testid="admin-passcode-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            className="w-full bg-transparent border-b border-ink/25 focus:border-bronze outline-none py-3 text-ink transition-colors mb-6"
          />
          {error && <p className="text-bronze font-medium mb-4" data-testid="admin-error">{error}</p>}
          <button
            onClick={unlock}
            data-testid="admin-unlock-button"
            className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-ink font-medium px-6 py-3 rounded-full transition-all"
          >
            {loading ? "…" : a.unlock}
          </button>
        </div>
      )}

      {leads && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <p className="font-mono text-sm text-stone-fg" data-testid="admin-count">{leads.length} leads</p>
            <button
              onClick={() => load(key)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-bronze transition-colors"
            >
              <RefreshCw size={15} aria-hidden /> ↻
            </button>
          </div>
          {leads.length === 0 ? (
            <p className="text-ink/60" data-testid="admin-empty">{a.empty}</p>
          ) : (
            <div className="space-y-4">
              {leads.map((lead, i) => (
                <article key={lead.id || i} className="bg-white border border-stone rounded-2xl p-6" data-testid={`lead-card-${i}`}>
                  <div className="flex flex-wrap justify-between gap-2 mb-3">
                    <p className="font-serif text-xl text-ink">{lead.name}</p>
                    <p className="font-mono text-xs text-stone-fg">
                      {lead.created_at ? new Date(lead.created_at).toLocaleString() : ""} · {lead.lang?.toUpperCase()}
                    </p>
                  </div>
                  <p className="text-bronze font-medium mb-3" data-testid={`lead-contact-${i}`}>{lead.contact}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-ink/75">
                    <p><span className="font-mono text-xs uppercase text-stone-fg">{a.situation}: </span>{lead.situation}</p>
                    <p><span className="font-mono text-xs uppercase text-stone-fg">{a.timeline}: </span>{lead.timeline}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {(lead.needs || []).map((n, j) => (
                      <span key={j} className="text-xs bg-olive-soft/60 text-olive-dark px-3 py-1 rounded-full">{n}</span>
                    ))}
                  </div>
                  {lead.message && <p className="mt-3 text-ink/70 italic">“{lead.message}”</p>}
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
